function debounce(fn, delay) {
  let timer;
  const debounced = function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;
  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    currentValue = from + (to - from) * easing(progress);
    onUpdate(currentValue);
    if (progress < 1) requestAnimationFrame(animate);
    else if (typeof onComplete === 'function') onComplete();
  }
  requestAnimationFrame(animate);
  return {
    get current() { return currentValue; },
    cancel() { cancelled = true; }
  };
}

class MarqueeComponent extends HTMLElement {
  #animation = null;
  #marqueeWidth = null;

  get refs() {
    return {
      wrapper: this.querySelector('[ref="wrapper"]'),
      content: this.querySelector('[ref="content"]'),
      marqueeItems: Array.from(this.querySelectorAll('[ref="marqueeItems[]"]'))
    };
  }

  async connectedCallback() {
    const { marqueeItems } = this.refs;
    if (!marqueeItems.length) return;
    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);
    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);
    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', () => this.#speedUp());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;
    const animation = this.refs.wrapper?.getAnimations()[0];
    if (!animation) return;
    this.#animation = animateValue({
      duration: 500, from: 1, to: 0,
      onUpdate: (v) => animation.updatePlaybackRate(v),
      onComplete: () => { this.#animation = null; }
    });
  }, 500);

  #speedUp() {
    this.#slowDown.cancel();
    const animation = this.refs.wrapper?.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;
    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();
    this.#animation = animateValue({
      duration: 500, from, to: 1,
      onUpdate: (v) => animation.updatePlaybackRate(v),
      onComplete: () => { this.#animation = null; }
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    if (!wrapper || !content) return null;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;
    return new Promise((resolve) => {
      if (!marqueeItems[0]) return setTimeout(() => resolve({ numberOfCopies: 1 }), 0);
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        observer.disconnect();
        const rootWidth = entry.rootBounds?.width ?? window.innerWidth;
        const itemsWidth = entry.boundingClientRect.width;
        const isHorizontalResize = this.#marqueeWidth !== rootWidth;
        this.#marqueeWidth = rootWidth;
        setTimeout(() => resolve({
          numberOfCopies: itemsWidth === 0 ? 1 : Math.ceil(rootWidth / itemsWidth),
          isHorizontalResize
        }), 0);
      }, { root: this });
      observer.observe(marqueeItems[0]);
    });
  }

  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();
    if (!isHorizontalResize) return;
    const current = marqueeItems.length;
    const speed = this.#calculateSpeed(numberOfCopies);
    if (numberOfCopies > current) this.#addRepeatedItems(numberOfCopies - current);
    else if (numberOfCopies < current) this.#removeRepeatedItems(current - numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper?.getAnimations() ?? [];
    requestAnimationFrame(() => { for (const a of animations) a.currentTime = 0; });
  }

  #duplicateContent() {
    this.clonedContent?.remove();
    const { content, wrapper } = this.refs;
    if (!content || !wrapper) return;
    const clone = content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    wrapper.appendChild(clone);
  }

  #addRepeatedItems(n) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;
    for (let i = 0; i < n - 1; i++) content.appendChild(marqueeItems[0].cloneNode(true));
  }

  #removeRepeatedItems(n) {
    const { content } = this.refs;
    const toRemove = Math.min(n, content.children.length - 1);
    for (let i = 0; i < toRemove; i++) content.lastElementChild?.remove();
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
